import "./History.css";
import { Button } from "./Button";
import { EmptyState } from "./Card";
import { Select } from "./Field";
import { durText } from "./Ops";
import type { Session } from "../chat/api";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 관측 › 이력(t-in-port-obs-history) ══
   끝난 이슈마다 토큰 · 소요를 돌아본다. 「끝남」은 **done 만**이다 — 취소는 「안 하기로 했다」라 돌아볼 일이 아니고,
   완료 시각 · 달성률이 done 전용인 것과 같은 축이다(AGENTS.md 「닫힘은 done ∪ cancelled」).

   토큰 · 소요는 그 이슈의 세션 기록을 더한다(소요 = 끝난 세션들이 돈 시간의 합 — 등록부터 완료까지가 아니다).
   세션이 없거나 하나도 토큰을 못 밝혔으면 **null(모름)**이고, 정렬에서 늘 뒤로 간다. */

export type HistRange = "all" | "1d" | "7d" | "30d" | "90d";
export const HIST_RANGES: HistRange[] = ["all", "1d", "7d", "30d", "90d"];
const DAY = 86_400_000;
const RANGE_MS: Record<Exclude<HistRange, "all">, number> = { "1d": DAY, "7d": 7 * DAY, "30d": 30 * DAY, "90d": 90 * DAY };

export type HistSort = "doneAt" | "tokens" | "ms" | "createdAt" | "title";
export const HIST_SORTS: HistSort[] = ["doneAt", "tokens", "ms", "createdAt", "title"];

export type HistRow = {
  id: string;
  title: string;
  projectId: string | null;
  doneAt: string | null;
  createdAt: string | null;
  tokens: number | null;
  ms: number | null;
  sessions: number;
};

type DoneIssue = Pick<Issue, "id" | "title" | "status" | "projectId" | "createdAt" | "childCount"> & { doneAt?: string | null };

/** 끝난 잎 이슈 × 그 세션들. 부모(에픽)는 하위에서 파생되니 세지 않는다 — 대시보드 진행률과 같은 셈. */
export function foldHistory(issues: DoneIssue[], sessions: Session[]): HistRow[] {
  const by = new Map<string, Session[]>();
  for (const s of sessions) by.set(s.taskId, [...(by.get(s.taskId) ?? []), s]);
  return issues
    .filter((t) => t.status === "done" && t.childCount === 0)
    .map((t) => {
      const ss = by.get(t.id) ?? [];
      let tokens: number | null = null;
      let ms: number | null = null;
      for (const s of ss) {
        if (s.inputTokens !== null || s.outputTokens !== null) tokens = (tokens ?? 0) + (s.inputTokens ?? 0) + (s.outputTokens ?? 0);
        if (s.startedAt && s.endedAt) ms = (ms ?? 0) + Math.max(0, Date.parse(s.endedAt) - Date.parse(s.startedAt));
      }
      return { id: t.id, title: t.title, projectId: t.projectId ?? null, doneAt: t.doneAt ?? null, createdAt: t.createdAt ?? null, tokens, ms, sessions: ss.length };
    });
}

export type HistFilter = { range: HistRange; project: string; sort: HistSort; asc: boolean };
export const HIST_DEFAULT: HistFilter = { range: "all", project: "", sort: "doneAt", asc: false };

/** 기간(완료 시각) · 프로젝트(`-` = 미분류)로 거르고 정렬한다. **값 없는 줄은 방향과 상관없이 늘 뒤로.** */
export function filterHistory(rows: HistRow[], f: HistFilter, now = Date.now()): HistRow[] {
  const from = f.range === "all" ? null : now - RANGE_MS[f.range];
  const kept = rows.filter((r) => {
    if (from !== null && (!r.doneAt || Date.parse(r.doneAt) < from)) return false;
    if (f.project === "-" ? r.projectId !== null : f.project && r.projectId !== f.project) return false;
    return true;
  });
  const key = (r: HistRow): number | string | null =>
    f.sort === "title" ? r.title : f.sort === "tokens" ? r.tokens : f.sort === "ms" ? r.ms : r[f.sort] ? Date.parse(r[f.sort] as string) : null;
  return kept.sort((a, b) => {
    const x = key(a);
    const y = key(b);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    const c = typeof x === "string" ? x.localeCompare(y as string, "ko") : x - (y as number);
    return f.asc ? c : -c;
  });
}

export type HistTotals = { n: number; measured: number; tokens: number; ms: number; timed: number; sessions: number };

export function histTotals(rows: HistRow[]): HistTotals {
  return rows.reduce(
    (t, r) => ({
      n: t.n + 1,
      measured: t.measured + (r.tokens !== null ? 1 : 0),
      tokens: t.tokens + (r.tokens ?? 0),
      ms: t.ms + (r.ms ?? 0),
      timed: t.timed + (r.ms !== null ? 1 : 0),
      sessions: t.sessions + r.sessions,
    }),
    { n: 0, measured: 0, tokens: 0, ms: 0, timed: 0, sessions: 0 },
  );
}

const num = (n: number | null) => (n === null ? tx("ops.unknown") : Math.round(n).toLocaleString("ko-KR"));
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleString("sv-SE", { timeZone: "Asia/Seoul" }).slice(0, 16) : tx("hist.noTime"));

/** 합계 / 이슈당 평균. 평균의 분모는 **값이 있는 이슈 수**다 — 모르는 이슈를 0 으로 넣으면 평균이 내려간다. */
export function HistStats({ t, avg }: { t: HistTotals; avg: boolean }) {
  const items: [string, string][] = avg
    ? [
        ["tokens", t.measured ? num(t.tokens / t.measured) : tx("ops.unknown")],
        ["ms", t.timed ? durText(t.ms / t.timed) : tx("ops.unknown")],
        ["sessions", t.n ? (Math.round((t.sessions / t.n) * 10) / 10).toString() : tx("ops.unknown")],
      ]
    : [
        ["tokens", t.measured ? num(t.tokens) : tx("ops.unknown")],
        ["ms", t.timed ? durText(t.ms) : tx("ops.unknown")],
        ["sessions", String(t.sessions)],
      ];
  return (
    <div className="hist-stats">
      {items.map(([k, v]) => (
        <div key={k} className="b-stat">
          <span className="caption">{tx(`hist.stat.${avg ? "avg" : "sum"}.${k as "tokens" | "ms" | "sessions"}`)}</span>
          <p className="b-stat-value">{v}</p>
        </div>
      ))}
    </div>
  );
}

export function HistFilterBar({
  value,
  projects,
  avg,
  onChange,
  onAvg,
}: {
  value: HistFilter;
  projects: { id: string; name: string }[];
  avg: boolean;
  onChange: (f: HistFilter) => void;
  onAvg: (v: boolean) => void;
}) {
  return (
    <div className="b-filterbar">
      <div className="b-filterbar-chips">
        {HIST_RANGES.map((r) => (
          <button key={r} type="button" className={`b-chip${value.range === r ? " is-on" : ""}`} aria-pressed={value.range === r} onClick={() => onChange({ ...value, range: r })}>
            {tx(`hist.range.${r}`)}
          </button>
        ))}
      </div>
      <div className="b-filterbar-chips">
        <Select size="md" value={value.project} aria-label={tx("hist.project")} onChange={(e) => onChange({ ...value, project: e.target.value })}>
          <option value="">{tx("hist.projectAll")}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          <option value="-">{tx("hist.projectNone")}</option>
        </Select>
        <Select size="md" value={value.sort} aria-label={tx("hist.sort")} onChange={(e) => onChange({ ...value, sort: e.target.value as HistSort })}>
          {HIST_SORTS.map((s) => (
            <option key={s} value={s}>
              {tx(`hist.sortBy.${s}`)}
            </option>
          ))}
        </Select>
        <Button size="md" aria-label={tx("hist.dir")} onClick={() => onChange({ ...value, asc: !value.asc })}>
          {value.asc ? `↑ ${tx("hist.asc")}` : `↓ ${tx("hist.desc")}`}
        </Button>
      </div>
      <div className="b-filterbar-end">
        <div className="b-tabs" role="tablist" aria-label={tx("hist.view")}>
          {[false, true].map((v) => (
            <button key={String(v)} type="button" role="tab" aria-selected={avg === v} className={`b-tab${avg === v ? " is-on" : ""}`} onClick={() => onAvg(v)}>
              {tx(v ? "hist.avg" : "hist.sum")}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HistTable({ rows, projectName, shown, onMore }: { rows: HistRow[]; projectName: (id: string | null) => string; shown: number; onMore: () => void }) {
  if (!rows.length) return <EmptyState title={tx("hist.empty")} desc={tx("hist.emptyDesc")} />;
  return (
    <>
      <div className="b-table-wrap">
        <table className="b-table">
          <thead>
            <tr>
              <th>{tx("ops.col.issue")}</th>
              <th>{tx("hist.col.project")}</th>
              <th>{tx("hist.col.doneAt")}</th>
              <th className="is-end">{tx("ops.sess.col.tokens")}</th>
              <th className="is-end">{tx("ops.usage.stat.dur")}</th>
              <th className="is-end">{tx("ops.usage.col.sessions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((r) => (
              <tr key={r.id}>
                <td className="is-primary hist-title">
                  <a href={`#/issue?id=${encodeURIComponent(r.id)}`}>{r.title}</a> <code className="muted">{r.id}</code>
                </td>
                <td>{r.projectId ? projectName(r.projectId) : <span className="muted">{tx("hist.projectNone")}</span>}</td>
                <td className="tnum">{day(r.doneAt)}</td>
                <td className="is-end">{num(r.tokens)}</td>
                <td className="is-end">{durText(r.ms)}</td>
                <td className="is-end">{r.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown ? (
        <div className="hist-more">
          <Button size="md" onClick={onMore}>
            {tx("ops.more", { n: rows.length - shown })}
          </Button>
        </div>
      ) : null}
    </>
  );
}
